import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../environments/environment";
import { BboxModel } from '../../../shared/models/bbox.model';
import { StreetSegmentMarkingsStatus, StreetSegmentStatus, StreetSegmentSurfaceStatus } from '../../../shared/models/street-segment-status.model';
import { StreetSegmentStatusKind } from '../../enum/street-segment-status-kind.enum';

@Injectable()
export class StreetSegmentStatusService {
  private baseUrl = environment.BASE_PATH;

  private cache = new Map<number, StreetSegmentStatus>();
  private reverseCache = new Map<string, number>();

  constructor(
    private readonly http: HttpClient,
  ) { }

  async findInRegion(bbox: BboxModel): Promise<Array<StreetSegmentStatus>> {
    const ids = await this.findIdsInRegion(bbox);

    const residualIds = new Array<number>();
    const cachedStatuses = new Array<StreetSegmentStatus>();

    for (const id of ids) {
      if (this.cache.has(id)) {
        cachedStatuses.push(this.cache.get(id)!);
      } else {
        residualIds.push(id);
      }
    }

    if (residualIds.length == 0) {
      return cachedStatuses;
    }

    const residualStatuses = await this.findStatusByIds(residualIds);
    residualStatuses.forEach((status) => this.cacheStatus(status));

    return cachedStatuses.concat(residualStatuses);
  }

  getSurfaceStatus(segmentId: number): StreetSegmentSurfaceStatus | undefined {
    const status = this.getCachedStatus(StreetSegmentStatusKind.SURFACE, segmentId);
    if (!status) {
      return undefined;
    }

    const surfaceStatus: StreetSegmentStatus = status;
    surfaceStatus.assertSurfaceStatus();

    return surfaceStatus;
  }

  getMarkingsStatus(segmentId: number): StreetSegmentMarkingsStatus | undefined {
    const status = this.getCachedStatus(StreetSegmentStatusKind.MARKINGS, segmentId);
    if (!status) {
      return undefined;
    }

    const markingsStatus: StreetSegmentStatus = status;
    markingsStatus.assertMarkingsStatus();

    return markingsStatus;
  }

  private async findIdsInRegion(bbox: BboxModel) {
    let url = `${this.baseUrl}/street-segment-status/ids`;

    const paramsObj = {
      maxLat: Math.max(bbox.ne.lat, bbox.sw.lat),
      minLat: Math.min(bbox.ne.lat, bbox.sw.lat),
      maxLon: Math.max(bbox.ne.lng, bbox.sw.lng),
      minLon: Math.min(bbox.ne.lng, bbox.sw.lng),
    };

    const params = new HttpParams({ fromObject: paramsObj });
    const result$ = this.http.get<Array<number>>(url, { params });
    return firstValueFrom(result$);
  }

  private async findStatusByIds(ids: number[]) {
    let url = `${this.baseUrl}/street-segment-status/by-ids`;
    const result$ = this.http.post<any[]>(url, { ids });
    const result = await firstValueFrom(result$);

    return result.map(StreetSegmentStatus.fromObject);
  }

  private generateReverseCacheKey(kind: StreetSegmentStatusKind, segmentId: number) {
    return `${kind}-${segmentId}`;
  }

  private cacheStatus(status: StreetSegmentStatus) {
    this.cache.set(status.id, status);

    const key = this.generateReverseCacheKey(status.kind, status.streetSegment?.id);
    this.reverseCache.set(key, status.id);
  }

  private getCachedStatus(kind: StreetSegmentStatusKind, segmentId: number) {
    const key = this.generateReverseCacheKey(kind, segmentId);
    const statusId = this.reverseCache.get(key);
    return statusId
      ? this.cache.get(statusId)
      : undefined;
  }
}
